import React, { useState, useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useLocation } from "react-router-dom";
import { yupResolver } from "@hookform/resolvers/yup";
import FormHandler from "../../Common/Form/FormHandler/FormHandler";
import { toast, ToastContainer } from "react-toastify";
import {
  createMasterSOPURL,
  updateMasterSOPURL,
  getTaskConfigsURL,
  getTaskListURLById,
} from "../../../../Services/EndPoints";
import {
  getData,
  postDataWithBody,
  putDataWithBody,
} from "../../../../Services/Services";
import {
  sopformschema,
  SoPFormConfig,
} from "../../../../JsonData/CreateTaskConfig";
import { Button } from "react-bootstrap";
import "./SOPForm.css";
import { useTranslation } from "react-i18next";
import { LayoutState } from "../../../../Context/LayoutContext/LayoutContext";

/**
 * SOPForm Component
 * Handles creation and editing of Standard Operating Procedures (SOPs)
 * Features: Dynamic form fields, task filtering, incident type selection, validation
 */
const SOPForm = () => {
  // Form state management
  const [isDisabled, setIsDisabled] = useState(false);
  const [SoPConfig, setSoPConfig] = useState(SoPFormConfig);
  const [allTasks, setAllTasks] = useState([]);
  const [configData, setConfigData] = useState(null);

  // Refs to track initialization state and prevent unnecessary re-renders
  const initializationComplete = useRef(false);
  const initialFormSetComplete = useRef(false);

  // Navigation and form setup
  const { state } = useLocation();
  const edit_data = state?.data;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);

  const { collapsed } = LayoutState();

  // React Hook Form setup with Yup validation
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    getValues,
    reset,
    control,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(sopformschema[activeTab]),
    mode: "all",
  });

  // Watch form fields for dynamic updates
  const selectedIncidentId = watch("incidentId");
  const selectedTaskType = watch("task_type");

  /**
   * Load initial configuration data on component mount
   * This must complete before any other data processing
   */
  useEffect(() => {
    const fetchConfigurations = async () => {
      try {
        const configRes = await getTaskConfigs();
        setConfigData(configRes);
        initializationComplete.current = true;
      } catch (error) {
        console.error("Error fetching configurations:", error);
        toast.error("Failed to load configuration data", {
          position: toast.POSITION.TOP_CENTER,
          autoClose: 2000,
        });
      }
    };

    fetchConfigurations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Handle edit mode setup after configurations are loaded
   * Populates form fields with existing SOP data and loads associated tasks
   */
  useEffect(() => {
    // Only proceed if config data is loaded and form hasn't been set up yet
    if (
      !configData ||
      initialFormSetComplete.current ||
      !initializationComplete.current
    )
      return;

    const setupEditData = async () => {
      try {
        if (edit_data) {
          // Find the corresponding incident ID from configuration data
          let incidentIdToSet = "";
          if (edit_data.incident_type) {
            const incidentConfig = configData.find(
              (config) =>
                config.category_name === "INCDNT_CAT" &&
                config.value === edit_data.incident_type
            );

            incidentIdToSet = incidentConfig
              ? incidentConfig.id.toString()
              : "";
          }

          // Prepare form data object with proper field mappings
          const formData = {
            id: edit_data.id,
            name: edit_data.name,
            description: edit_data.description,
            incidentId: incidentIdToSet,
            task_type: edit_data.task_type_id === 341 ? "0" : "1", // Map task type ID to dropdown value
          };

          reset(formData);

          // Fetch tasks for the selected incident
          if (incidentIdToSet) {
            const taskList = await fetchTaskListData(incidentIdToSet);
            setAllTasks(taskList);

            // Apply task type filter if available
            if (formData.task_type) {
              filterAndUpdateTasksByType(formData.task_type, taskList);
            }

            // Set selected tasks with delay to ensure options are updated
            if (edit_data.task_id_list && edit_data.task_list) {
              setTimeout(() => {
                populateAttachedTasks(edit_data);
              }, 100);
            }
          }
        }

        initialFormSetComplete.current = true;
      } catch (error) {
        console.error("Error setting up edit data:", error);
      }
    };

    setupEditData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configData, edit_data]);
  console.log(setActiveTab);
  /**
   * Handle incident type selection changes
   * Loads tasks associated with the selected incident type
   */
  useEffect(() => {
    // Skip if initialization is not complete
    if (!initializationComplete.current) return;

    // Skip if no incident ID selected or in the middle of initial form setup
    if (!selectedIncidentId || !initialFormSetComplete.current) return;

    const handleIncidentChange = async () => {
      try {
        // Clear current tasks selection
        setValue("sop_task", []);

        // Fetch tasks for the selected incident
        const taskList = await fetchTaskListData(selectedIncidentId);
        setAllTasks(taskList);

        // Apply task type filter if available
        if (selectedTaskType) {
          filterAndUpdateTasksByType(selectedTaskType, taskList);
        }
      } catch (error) {
        console.error("Error handling incident change:", error);
      }
    };

    handleIncidentChange();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIncidentId]);

  /**
   * Handle task type selection changes
   * Filters available tasks based on selected type (Activity/Notification)
   */
  useEffect(() => {
    // Skip if initialization is not complete or in the middle of initial form setup
    if (!initializationComplete.current || !initialFormSetComplete.current)
      return;

    // Skip if no tasks loaded
    if (allTasks.length === 0) return;

    if (selectedTaskType) {
      setValue("sop_task", []);
      filterAndUpdateTasksByType(selectedTaskType, allTasks);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTaskType, allTasks.length]);

  /**
   * Fetch task configuration data from the server
   * Includes incident categories, task priorities, statuses, and types
   * @returns {Promise<Array>} Configuration data array
   */
  async function getTaskConfigs() {
    try {
      const res = await postDataWithBody(
        getTaskConfigsURL,
        ["TSK_PRIOR", "INCDNT_CAT", "TSK_STAT", "TSK_TYP"],
        {}
      );

      // Process configuration data into grouped structure
      const config_type = {};
      res.forEach((config) => {
        if (!config_type[config.category_name]) {
          config_type[config.category_name] = [];
        }

        config_type[config.category_name].push({
          label: config.value,
          value: config.id,
        });
      });

      // Update SoPConfig with incident categories for dropdown
      const updatedConfig = [...SoPConfig];
      const newFields = [...updatedConfig[0].fields];

      // Update incident field options
      newFields[0] = {
        ...newFields[0],
        options: config_type["INCDNT_CAT"] || [],
      };

      updatedConfig[0] = {
        ...updatedConfig[0],
        fields: newFields,
      };

      setSoPConfig(updatedConfig);

      return res;
    } catch (error) {
      console.error("Error fetching task configs:", error);
      toast.error("Failed to fetch configuration data", {
        position: toast.POSITION.TOP_CENTER,
        autoClose: 2000,
      });
      throw error;
    }
  }

  /**
   * Filter and update task options based on selected task type
   */
  function filterAndUpdateTasksByType(taskType, tasks = allTasks) {
    try {
      // Convert taskType value to label
      const taskTypeLabel = taskType === "0" ? "Activity" : "Notification";

      // Filter tasks based on task_type
      const filteredTasks = tasks.filter(
        (task) => task.task_type === taskTypeLabel
      );

      // Format tasks for dropdown options
      const taskOptions = filteredTasks.map((task) => ({
        label: task.task_name,
        value: task.id.toString(),
      }));

      // Update config with filtered tasks - preserve all other fields
      setSoPConfig((prevConfig) => {
        // Create deep copies to avoid reference issues
        const updatedConfig = JSON.parse(JSON.stringify(prevConfig));

        // Only update the task field (index 2) and preserve all other fields
        if (
          updatedConfig[0] &&
          updatedConfig[0].fields &&
          updatedConfig[0].fields[2]
        ) {
          updatedConfig[0].fields[2].options = taskOptions;
        }

        return updatedConfig;
      });
    } catch (error) {
      console.error("Error filtering tasks:", error);
    }
  }

  /**
   * Fetch task list data based on incident ID
   */
  async function fetchTaskListData(incident_id) {
    try {
      if (!incident_id) return [];

      // Build API URL with incident ID
      const taskListUrl = getTaskListURLById.replace("ID", incident_id);
      const result = await getData(taskListUrl);
      return result;
    } catch (error) {
      console.error("Error fetching task list:", error);
      toast.error("Failed to fetch tasks", {
        position: toast.POSITION.TOP_CENTER,
        autoClose: 2000,
      });
      return [];
    }
  }

  /**
   * Populate form with attached tasks from edit data
   */
  function populateAttachedTasks(data) {
    try {
      if (!data.task_id_list || !data.task_list) {
        return;
      }

      // Parse comma-separated task IDs and names
      const taskIds = data.task_id_list.split(",");
      const taskNames = data.task_list.split(",");

      // Create selected tasks array for multi-select dropdown
      const selectedTasks = taskIds.map((id, index) => ({
        value: id.trim(),
        label: (taskNames[index] || id).trim(),
      }));

      // Set form value if tasks exist
      if (selectedTasks.length > 0) {
        setValue("sop_task", selectedTasks);
      }
    } catch (error) {
      console.error("Error populating tasks:", error);
    }
  }

  /**
   * Generate form fields using FormHandler component
   */
  const GenerateForm = (fields, register, errors, control) => {
    return (
      <div>
        <FormHandler
          register={register}
          errors={errors}
          fields={fields}
          control={control}
          columns={2}
          getValues={getValues}
        />
      </div>
    );
  };

  /**
   * Generate tab data with form components
   */
  const generateTabsData = (tabWiseData) => {
    return tabWiseData.map((element) => ({
      ...element,
      component: GenerateForm(element.fields, register, errors, control),
    }));
  };

  const tabsData = generateTabsData(SoPConfig);

  /**
   * Navigate to create task page
   */
  const navToCreateTask = () => {
    navigate("../create-task");
  };

  /**
   * Navigate to create resource page
   */
  // const navToCreateResource = () => {
  //   navigate("../../resource-list/create-resource");
  // };

  /**
   * Handle form submission for creating or updating SOP
   */
  const submitHandler = async (data, e) => {
    e.preventDefault();
    setIsDisabled(true);

    try {
      data.status = true;

      // Process selected tasks into required format
      const sop_task_map =
        data.sop_task?.map((task) => ({
          task_id: task.value,
          ...(edit_data ? { sop_id: data.id } : {}), // Include SOP ID for updates
        })) || [];

      data.sop_task_map = sop_task_map;

      // Submit data based on edit/create mode
      let res;
      if (edit_data) {
        res = await putDataWithBody(updateMasterSOPURL, data, {});
      } else {
        res = await postDataWithBody(createMasterSOPURL, data, {});
      }

      // Handle API response
      if (res.status === "success") {
        toast.success(res.msg, {
          position: toast.POSITION.TOP_CENTER,
          autoClose: 2000,
        });
        navigate("/sop-management/view-sop");
      } else {
        toast.error(res.msg, {
          position: toast.POSITION.TOP_CENTER,
          autoClose: 2000,
        });
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error("An error occurred while saving the SOP", {
        position: toast.POSITION.TOP_CENTER,
        autoClose: 2000,
      });
    } finally {
      setIsDisabled(false);
    }
  };

  return (
    <div className="mt-4 facility-container">
      <div className="form-body">
        <div className="registration__form--body">
          {/* Navigation buttons */}
          <div
            onClick={navToCreateTask}
            className={`add_task_sop_mgmnt ${collapsed ? "" : "expanded"}`}
          >
            {t("addtask")}
          </div>

          {/* Main form */}
          <form onSubmit={handleSubmit(submitHandler)}>
            <div>
              {/* Render active tab content */}
              {tabsData?.map((tab) => (
                <React.Fragment key={tab.id}>
                  {tab.id === activeTab && <div>{tab.component}</div>}
                </React.Fragment>
              ))}
            </div>

            {/* Submit button - only show on last tab */}
            <div className="d-flex justify-content-center">
              {tabsData.length - 1 === activeTab && (
                <Button
                  type="submit"
                  className="mx-2"
                  variant="primary"
                  disabled={isDisabled}
                >
                  {edit_data ? t("update") : t("saveAndSubmit")}
                </Button>
              )}
            </div>
          </form>
        </div>
      </div>

      {/* Toast notifications container */}
      <ToastContainer />
    </div>
  );
};

export default SOPForm;
